import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/system';

const ManagePopularCars = () => {
  const [allCars, setAllCars] = useState([]);
  const [popularCars, setPopularCars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);

  // New state for viewing popular cars
  const [showPopularView, setShowPopularView] = useState(false);
  const [popularSearchTerm, setPopularSearchTerm] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ show: false, car: null });

  // Toast notification state
  const [toast, setToast] = useState({ show: false, message: '', type: '' });

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: '' });
    }, 3000);
  };

  const SpinningLogo = styled("img")(({ theme }) => ({
    width: "150px",
    height: "150px",
  }));

  // Fetch all cars
  const fetchAllCars = async (page = 0) => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://car.amar.co.jp/api/master/popular-car/get-available-models?page=${page}&size=100`,
        {
          headers: {
            'accept': 'application/json',
            'Accept-Language': 'en'
          }
        }
      );
      const result = await response.json();

      if (result.success) {
        setAllCars(result.data.carModels || []);
        setTotalPages(result.data.totalPages || 0);
        setTotalRecords(result.data.totalRecords || 0);
        setHasNext(result.data.hasNext || false);
        setHasPrevious(result.data.hasPrevious || false);
      }
    } catch (error) {
      console.error('Error fetching cars:', error);
      showToast('Failed to fetch cars', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch popular cars
  const fetchPopularCars = async () => {
    try {
      const response = await fetch(
        'https://car.amar.co.jp/api/master/popular-car/get-all_popular_car?page=0&size=500',
        {
          headers: {
            'accept': 'application/json',
            'Accept-Language': 'en'
          }
        }
      );
      const result = await response.json();
      setPopularCars(result.data || []);
    } catch (error) {
      console.error('Error fetching popular cars:', error);
    }
  };

  useEffect(() => {
    fetchAllCars(currentPage);
    fetchPopularCars();
  }, [currentPage]);

  // Add to popular - with real-time updates
  const addToPopular = async (carModelId) => {
    try {
      const response = await fetch(
        `https://car.amar.co.jp/api/master/popular-car/add-to-popular?carModelId=${carModelId}`,
        {
          method: 'POST',
          headers: {
            'accept': 'application/json'
          },
          body: ''
        }
      );

      const result = await response.json();

      if (response.ok) {
        showToast('Added to popular section successfully', 'success');

        const addedCar = allCars.find(car => car.carModelId === carModelId);
        if (addedCar) {
          setPopularCars(prev => [...prev, addedCar]);
          setAllCars(prev => prev.filter(car => car.carModelId !== carModelId));
          setTotalRecords(prev => prev - 1);
        }
      } else {
        showToast(result.message || 'Failed to add car', 'error');
      }
    } catch (error) {
      console.error('Error adding to popular:', error);
      showToast('Error adding car to popular section', 'error');
    }
  };

  // Remove from popular
  const removeFromPopular = async (popularCarId) => {
    try {
      const response = await fetch(
        `https://car.amar.co.jp/api/master/popular-car/remove-from-popular?popularCarId=${popularCarId}`,
        {
          method: 'DELETE',
          headers: {
            'accept': 'application/json'
          }
        }
      );

      if (response.ok) {
        showToast('Removed from popular section', 'success');
        setPopularCars(prev => prev.filter(car => car.popularCarId !== popularCarId));
      } else {
        const result = await response.json();
        showToast(result.message || 'Failed to remove car', 'error');
      }
    } catch (error) {
      console.error('Error removing from popular:', error);
      showToast('Error removing car', 'error');
    }
    setDeleteDialog({ show: false, car: null });
  };

  const isPopular = (carModelId) => {
    return popularCars.some(car => car.carModelId === carModelId);
  };

  // Filter out popular cars from all cars and apply search
  const availableCars = allCars.filter(car => !isPopular(car.carModelId));

  const filteredCars = availableCars.filter(car =>
    car.carModelName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    car.carBrandName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getPageNumbers = () => {
    const pages = [];
    const delta = 2;
    const range = [];

    for (
      let i = Math.max(0, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (range[0] > 0) {
      pages.push(0);
      if (range[0] > 1) pages.push('...');
    }

    pages.push(...range);

    if (range[range.length - 1] < totalPages - 1) {
      if (range[range.length - 1] < totalPages - 2) pages.push('...');
      pages.push(totalPages - 1);
    }

    return pages;
  };

  // Filter popular cars for the view
  const filteredPopularCars = popularCars.filter(car =>
    car.carModelName?.toLowerCase().includes(popularSearchTerm.toLowerCase()) ||
    car.carBrandName?.toLowerCase().includes(popularSearchTerm.toLowerCase()) ||
    car.carCategoriesName?.toLowerCase().includes(popularSearchTerm.toLowerCase())
  );

  const styles = {
    container: {
      padding: '15px',
      maxWidth: '1400px',
      margin: '0 auto',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      background: '#f5f5f5',
      minHeight: '100vh'
    },
    header: {
      marginBottom: '20px',
      paddingBottom: '15px',
      borderBottom: '2px solid #e0e0e0'
    },
    title: {
      fontSize: '24px',
      color: '#2c3e50',
      marginBottom: '5px',
      fontWeight: '600'
    },
    viewToggle: {
      display: 'flex',
      gap: '10px',
      marginTop: '15px',
      marginBottom: '10px'
    },
    toggleBtn: {
      padding: '10px 20px',
      border: '2px solid #eb2329',
      borderRadius: '6px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      fontSize: '13px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    toggleBtnActive: {
      background: 'linear-gradient(135deg, #eb2329 0%, #c41e24 100%)',
      color: 'white',
      boxShadow: '0 4px 12px rgba(235, 35, 41, 0.3)'
    },
    toggleBtnInactive: {
      background: 'white',
      color: '#eb2329'
    },
    statsBar: {
      background: 'linear-gradient(135deg, #eb2329 0%, #c41e24 100%)',
      padding: '12px 20px',
      borderRadius: '8px',
      display: 'flex',
      gap: '25px',
      marginTop: '12px',
      flexWrap: 'wrap',
      boxShadow: '0 4px 15px rgba(235, 35, 41, 0.25)',
      border: '1px solid rgba(235, 35, 41, 0.2)'
    },
    stat: {
      display: 'flex',
      flexDirection: 'column',
      gap: '2px'
    },
    statLabel: {
      fontSize: '11px',
      color: 'rgba(255,255,255,0.9)',
      fontWeight: '500',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    statValue: {
      fontSize: '20px',
      color: 'white',
      fontWeight: 'bold',
      transition: 'all 0.3s ease'
    },
    topBar: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '15px',
      gap: '10px',
      flexWrap: 'wrap'
    },
    searchInput: {
      padding: '8px 12px',
      border: '1px solid #ddd',
      borderRadius: '6px',
      width: '280px',
      fontSize: '13px',
      outline: 'none',
      transition: 'all 0.2s',
      background: 'white',
      color: '#2c3e50'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
      gap: '12px',
      marginBottom: '20px'
    },
    popularGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
      gap: '15px',
      marginBottom: '20px'
    },
    card: {
      background: 'white',
      borderRadius: '8px',
      overflow: 'hidden',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      transition: 'all 0.2s ease',
      cursor: 'pointer',
      border: '1px solid #e0e0e0'
    },
    popularCard: {
      background: 'white',
      borderRadius: '8px',
      overflow: 'hidden',
      boxShadow: '0 4px 15px rgba(235, 35, 41, 0.15)',
      border: '2px solid #eb2329',
      transition: 'transform 0.2s ease'
    },
    imageContainer: {
      position: 'relative',
      height: '130px',
      overflow: 'hidden',
      background: '#f8f8f8'
    },
    popularImageContainer: {
      position: 'relative',
      height: '180px',
      overflow: 'hidden',
      background: '#f8f8f8'
    },
    image: {
      width: '100%',
      height: '100%',
      objectFit: 'cover'
    },
    badge: {
      position: 'absolute',
      top: '10px',
      left: '10px',
      background: 'linear-gradient(135deg, #eb2329 0%, #ff4444 100%)',
      color: 'white',
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '11px',
      fontWeight: '600',
      boxShadow: '0 2px 8px rgba(235, 35, 41, 0.5)',
      border: '1px solid rgba(255, 255, 255, 0.2)'
    },
    cardInfo: {
      padding: '10px'
    },
    cardTitle: {
      fontSize: '13px',
      color: '#2c3e50',
      marginBottom: '4px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      fontWeight: '600'
    },
    cardBrand: {
      color: '#7f8c8d',
      fontSize: '11px',
      marginBottom: '6px'
    },
    cardPrice: {
      fontSize: '15px',
      fontWeight: '700',
      color: '#eb2329',
      marginBottom: '8px'
    },
    button: {
      padding: '7px 14px',
      border: 'none',
      borderRadius: '6px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      fontSize: '12px',
      width: '100%',
      textTransform: 'uppercase',
      letterSpacing: '0.3px'
    },
    addBtn: {
      background: '#eb2329',
      color: 'white',
      boxShadow: '0 2px 4px rgba(235, 35, 41, 0.3)'
    },
    removeBtn: {
      background: '#dc3545',
      color: 'white',
      boxShadow: '0 2px 4px rgba(220, 53, 69, 0.3)'
    },
    pagination: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '15px',
      marginTop: '20px',
      padding: '15px',
      background: 'white',
      borderRadius: '8px',
      border: '1px solid #e0e0e0'
    },
    paginationBtn: {
      padding: '8px 16px',
      background: '#eb2329',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontWeight: '600',
      fontSize: '12px',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(235, 35, 41, 0.3)'
    },

    pageNumBtn: {
      padding: '8px 13px',
      background: 'white',
      color: ' #0b0b0bff',
      border: '1px solid #0b0b0bff',
      borderRadius: '6px',
      cursor: 'pointer',
      fontWeight: '600',
      fontSize: '12px',
      transition: 'all 0.2s ease',
      minWidth: '38px'
    },
    pageNumBtnActive: {
      background: '#eb2329',
      color: 'white',
      boxShadow: '0 2px 8px rgba(235, 35, 41, 0.3)'
    },
    pageEllipsis: {
      padding: '8px 4px',
      color: '#7f8c8d',
      fontWeight: '600',
      fontSize: '12px'
    },

    paginationBtnDisabled: {
      background: '#ddd',
      cursor: 'not-allowed',
      boxShadow: 'none',
      color: '#999'
    },
    paginationInfo: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '2px'
    },
    loading: {
      textAlign: 'center',
      padding: '30px',
      color: '#7f8c8d',
      fontSize: '14px'
    },
    emptyState: {
      textAlign: 'center',
      padding: '40px 20px',
      color: '#95a5a6'
    },
    toast: {
      position: 'fixed',
      top: '20px',
      right: '20px',
      padding: '12px 20px',
      borderRadius: '8px',
      color: 'white',
      fontWeight: '500',
      fontSize: '13px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
      zIndex: 10000,
      animation: 'slideIn 0.3s ease',
      minWidth: '250px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    toastSuccess: {
      background: 'linear-gradient(135deg, #eb2329 0%, #c41e24 100%)'
    },
    toastError: {
      background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)'
    },
    toastWarning: {
      background: 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)'
    },
    toastIcon: {
      fontSize: '18px'
    },
    dialogOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '20px'
    },
    dialog: {
      background: 'white',
      borderRadius: '12px',
      padding: '0',
      maxWidth: '450px',
      width: '100%',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
      overflow: 'hidden',
      border: '3px solid #eb2329'
    },
    dialogHeader: {
      background: 'linear-gradient(135deg, #eb2329 0%, #c41e24 100%)',
      padding: '20px 24px',
      borderBottom: '2px solid rgba(255, 255, 255, 0.1)'
    },
    dialogTitle: {
      color: 'white',
      fontSize: '20px',
      fontWeight: '700',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    dialogBody: {
      padding: '24px'
    },
    dialogMessage: {
      color: '#2c3e50',
      fontSize: '15px',
      lineHeight: '1.6',
      marginBottom: '16px'
    },
    dialogCarInfo: {
      background: '#f8f9fa',
      padding: '16px',
      borderRadius: '8px',
      marginBottom: '16px',
      border: '2px solid #e9ecef'
    },
    dialogCarName: {
      fontSize: '16px',
      fontWeight: '700',
      color: '#eb2329',
      marginBottom: '8px'
    },
    dialogCarDetails: {
      fontSize: '13px',
      color: '#6c757d',
      display: 'flex',
      flexDirection: 'column',
      gap: '4px'
    },
    dialogWarning: {
      background: '#fff3cd',
      border: '2px solid #ffc107',
      borderRadius: '8px',
      padding: '12px 16px',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      marginBottom: '20px'
    },
    dialogWarningIcon: {
      fontSize: '20px',
      color: '#ff9800',
      flexShrink: 0
    },
    dialogWarningText: {
      fontSize: '13px',
      color: '#856404',
      lineHeight: '1.5',
      margin: 0
    },
    dialogActions: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-end'
    },
    dialogButton: {
      padding: '10px 24px',
      border: 'none',
      borderRadius: '6px',
      fontWeight: '600',
      cursor: 'pointer',
      fontSize: '14px',
      transition: 'all 0.2s ease',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    dialogCancelBtn: {
      background: 'white',
      color: '#6c757d',
      border: '2px solid #dee2e6'
    },
    dialogDeleteBtn: {
      background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
      color: 'white',
      border: 'none',
      boxShadow: '0 4px 12px rgba(220, 53, 69, 0.3)'
    }
  };

  return (
    <div style={styles.container}>
      {/* Toast Notification */}
      {toast.show && (
        <div style={{
          ...styles.toast,
          ...(toast.type === 'success' ? styles.toastSuccess :
            toast.type === 'error' ? styles.toastError :
              styles.toastWarning)
        }}>
          <span style={styles.toastIcon}>
            {toast.type === 'success' ? '✓' : toast.type === 'error' ? '✕' : '⚠'}
          </span>
          <span>{toast.message}</span>
        </div>
      )}

      {/* Delete Dialog */}
      {deleteDialog.show && (
        <div style={styles.dialogOverlay} onClick={() => setDeleteDialog({ show: false, car: null })}>
          <div style={styles.dialog} onClick={(e) => e.stopPropagation()}>
            <div style={styles.dialogHeader}>
              <h3 style={styles.dialogTitle}>
                <span>⚠️</span>
                Remove From Popular
              </h3>
            </div>
            <div style={styles.dialogBody}>
              <p style={styles.dialogMessage}>
                Are you sure you want to remove this car from the popular section?
              </p>

              {deleteDialog.car && (
                <div style={styles.dialogCarInfo}>
                  <div style={styles.dialogCarName}>{deleteDialog.car.carModelName}</div>
                  <div style={styles.dialogCarDetails}>
                    <span>Brand: {deleteDialog.car.carBrandName || deleteDialog.car.carCategoriesName}</span>
                    <span>Price: ${deleteDialog.car.carPrice?.toLocaleString()}</span>
                  </div>
                </div>
              )}

              <div style={styles.dialogWarning}>
                <span style={styles.dialogWarningIcon}>⚠</span>
                <p style={styles.dialogWarningText}>
                  This action cannot be undone. The car will be removed from the popular section immediately.
                </p>
              </div>

              <div style={styles.dialogActions}>
                <button
                  style={{ ...styles.dialogButton, ...styles.dialogCancelBtn }}
                  onClick={() => setDeleteDialog({ show: false, car: null })}
                >
                  Cancel
                </button>
                <button
                  style={{ ...styles.dialogButton, ...styles.dialogDeleteBtn }}
                  onClick={() => removeFromPopular(deleteDialog.car.popularCarId)}
                >
                  Remove Car
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <h2 style={styles.title}>Manage Popular Cars</h2>

        {/* View Toggle Buttons */}
        <div style={styles.viewToggle}>
          <button
            style={{
              ...styles.toggleBtn,
              ...(showPopularView ? styles.toggleBtnInactive : styles.toggleBtnActive)
            }}
            onClick={() => setShowPopularView(false)}
          >
            📝 Add Popular Cars ({totalRecords})
          </button>
          <button
            style={{
              ...styles.toggleBtn,
              ...(showPopularView ? styles.toggleBtnActive : styles.toggleBtnInactive)
            }}
            onClick={() => setShowPopularView(true)}
          >
            ⭐ View Popular Cars ({popularCars.length})
          </button>
        </div>

        {/* Statistics */}
        <div style={styles.statsBar}>
          <div style={styles.stat}>
            <span style={styles.statLabel}>Total Available</span>
            <span style={styles.statValue}>{totalRecords.toLocaleString()}</span>
          </div>
          <div style={styles.stat}>
            <span style={styles.statLabel}>Already Popular</span>
            <span style={styles.statValue}>{popularCars.length}</span>
          </div>
          {!showPopularView && (
            <div style={styles.stat}>
              <span style={styles.statLabel}>Page</span>
              <span style={styles.statValue}>{currentPage + 1}/{totalPages}</span>
            </div>
          )}
        </div>
      </div>

      {/* Conditional Content Based on View */}
      {!showPopularView ? (
        // Available Cars Section (Add Popular)
        <div>
          <div style={styles.topBar}>
            <h3 style={{ fontSize: '16px', color: '#2c3e50', fontWeight: '600', margin: 0 }}>
              Available Cars (Not in Popular)
            </h3>
            <input
              type="text"
              placeholder="Search available cars..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={styles.searchInput}
            />
          </div>

          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "50vh",
                flexDirection: "column",
                gap: 2
              }}
            >
              <SpinningLogo src="/image/footer-logo-small.svg" alt="Loading" />
              <Typography variant="body1">Loading car data...</Typography>
            </Box>
          ) : (
            <>
              {filteredCars.length === 0 ? (
                <div style={styles.emptyState}>
                  <p style={{ fontSize: '14px', marginBottom: '5px' }}>
                    {searchTerm ? 'No cars match your search' : 'No available cars to add'}
                  </p>
                  <p style={{ fontSize: '12px' }}>
                    {searchTerm ? 'Try a different search term' : 'All cars are already in popular section'}
                  </p>
                </div>
              ) : (
                <div style={styles.grid}>
                  {filteredCars.map((car) => (
                    <div key={car.carModelId} style={styles.card}>
                      <div style={styles.imageContainer}>
                        <img
                          src={`https://car.amar.co.jp/api/image/car-exterior-images-1/get-image/${car.carModelId}`}
                          alt={car.carModelName}
                          onError={(e) => e.target.src = '/carousel/carslider.png'}
                          style={styles.image}
                        />
                      </div>
                      <div style={styles.cardInfo}>
                        <h4 style={styles.cardTitle}>{car.carModelName}</h4>
                        <p style={styles.cardBrand}>{car.carBrandName}</p>
                        <p style={styles.cardPrice}>${car.carPrice?.toLocaleString()}</p>
                        <button
                          style={{ ...styles.button, ...styles.addBtn }}
                          onClick={() => addToPopular(car.carModelId)}
                        >
                          Add to Popular
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Pagination */}
        
              <div style={styles.pagination}>
                <button
                  style={{
                    ...styles.paginationBtn,
                    ...(!hasPrevious && styles.paginationBtnDisabled)
                  }}
                  onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                  disabled={!hasPrevious}
                >
                  Previous
                </button>

                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {getPageNumbers().map((page, idx) =>
                    page === '...' ? (
                      <span key={`ellipsis-${idx}`} style={styles.pageEllipsis}>...</span>
                    ) : (
                      <button
                        key={page}
                        style={{
                          ...styles.pageNumBtn,
                          ...(page === currentPage && styles.pageNumBtnActive)
                        }}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page + 1}
                      </button>
                    )
                  )}
                </div>

                <button
                  style={{
                    ...styles.paginationBtn,
                    ...(!hasNext && styles.paginationBtnDisabled)
                  }}
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  disabled={!hasNext}
                >
                  Next
                </button>
              </div>
            </>
          )}
        </div>
      ) : (
        // Popular Cars View Section
        <div>
          <div style={styles.topBar}>
            <h3 style={{ fontSize: '16px', color: '#2c3e50', fontWeight: '600', margin: 0 }}>
              Current Popular Cars
            </h3>
            <input
              type="text"
              placeholder="Search popular cars..."
              value={popularSearchTerm}
              onChange={(e) => setPopularSearchTerm(e.target.value)}
              style={styles.searchInput}
            />
          </div>

          {filteredPopularCars.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={{ fontSize: '64px', marginBottom: '20px', opacity: 0.3 }}>🚗</div>
              <p style={{ fontSize: '18px', marginBottom: '10px', color: '#7f8c8d' }}>
                {popularSearchTerm ? 'No cars match your search' : 'No popular cars added yet'}
              </p>
              <p style={{ fontSize: '14px', color: '#95a5a6' }}>
                {popularSearchTerm ? 'Try a different search term' : 'Switch to "Add Popular Cars" tab to add cars'}
              </p>
            </div>
          ) : (
            <div style={styles.popularGrid}>
              {filteredPopularCars.map((car, index) => (
                <div key={car.carModelId} style={styles.popularCard}>
                  <div style={styles.popularImageContainer}>
                    <img
                      src={`https://car.amar.co.jp/api/image/car-exterior-images-1/get-image/${car.carModelId}`}
                      alt={car.carModelName}
                      onError={(e) => e.target.src = '/carousel/carslider.png'}
                      style={styles.image}
                    />
                    <span style={styles.badge}>#{index + 1}</span>
                  </div>
                  <div style={styles.cardInfo}>
                    <h4 style={styles.cardTitle}>{car.carModelName}</h4>
                    <p style={styles.cardBrand}>{car.carBrandName || car.carCategoriesName}</p>
                    <p style={styles.cardPrice}>${car.carPrice?.toLocaleString()}</p>
                    <button
                      style={{ ...styles.button, ...styles.removeBtn }}
                      onClick={() => setDeleteDialog({ show: true, car: car })}
                    >
                      Remove from Popular
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ManagePopularCars;